import { ChangeEvent, useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import { gameService } from "services/game/game.service"
import { CategoryRecord } from "services/game/type"

import { SectionOrderEditor } from "../SectionOrderEditor"

interface SectionsTabProps {
  geo: string
}

const titleEnOf = (c: CategoryRecord): string => {
  if (c.title && typeof c.title === "object") {
    return c.title.en ?? c.key
  }
  return c.key
}

export const SectionsTab = ({ geo }: SectionsTabProps) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const [categories, setCategories] = useState<CategoryRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  // CSV export/import — bulk over ALL (category, geo) groups. REPLACEs each
  // group present in the CSV; groups not in the file are untouched. Unknown
  // game identifiers are silently skipped (the count is shown in the toast).
  const csvInputRef = useRef<HTMLInputElement | null>(null)
  const [exportingCsv, setExportingCsv] = useState(false)
  const [importingCsv, setImportingCsv] = useState(false)

  const handleExportCsv = async () => {
    if (exportingCsv) return
    setExportingCsv(true)
    await gameService.exportSectionGameOrdersCsv()
    setExportingCsv(false)
  }

  const handleImportCsv = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return
    setImportingCsv(true)
    const result = await gameService.importSectionGameOrdersCsv(file)
    setImportingCsv(false)
    if (result) setRefreshKey((k) => k + 1)
  }

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      const data = await gameService.listCategories()
      if (cancelled) return
      setLoading(false)
      if (data) setCategories(data)
    }
    load()
    return () => {
      cancelled = true
    }
  }, [refreshKey])

  if (editingKey) {
    const editing = categories.find((c) => c.key === editingKey)
    return (
      <SectionOrderEditor
        sectionKey={editingKey}
        geo={geo}
        allowAddRemove={editing?.source_type === "MANUAL"}
        onBack={() => {
          setEditingKey(null)
          setRefreshKey((k) => k + 1)
        }}
      />
    )
  }

  // Orderable categories appear as clickable cards. Non-orderable ones (BUILTIN
  // recent/new with dynamic membership) are shown disabled so admins can see
  // they exist but understand why they can't be edited.
  const orderable = categories.filter((c) => c.is_orderable && c.source_type !== "RULE")
  const nonOrderable = categories.filter((c) => !c.is_orderable || c.source_type === "RULE")

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-1 max-w-3xl">
          <h2 className="font-semibold text-xl text-[rgba(6,7,21,1)] dark:text-white">
            {t("gameManagement.sections.title", { defaultValue: "Sections" })}
          </h2>
          <p className="text-sm opacity-70 text-[rgba(6,7,21,1)] dark:text-white">
            {t("gameManagement.sections.description", {
              defaultValue:
                "Click a section to edit its game order. BUILTIN sections (slots, live, …) include every qualifying game from a filter, and admin reorders are editorial overrides. MANUAL sections are open to add/remove games."
            })}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={exportingCsv || loading}
            className="btn btn-outline btn-sm border-black/10 text-[rgba(6,7,21,1)] hover:bg-black/[0.05] dark:text-white dark:border-white/10 dark:hover:bg-white/[0.06] disabled:opacity-40"
            title={t("gameManagement.sections.exportTip", {
              defaultValue: "Download GameCategoryOrder rows for every (category, geo) as CSV"
            })}
          >
            {exportingCsv
              ? t("gameManagement.common.exporting", { defaultValue: "Exporting…" })
              : t("gameManagement.common.exportCsv", { defaultValue: "Export CSV" })}
          </button>
          <label
            className={`btn btn-outline btn-sm border-black/10 text-[rgba(6,7,21,1)] hover:bg-black/[0.05] dark:text-white dark:border-white/10 dark:hover:bg-white/[0.06] ${
              importingCsv ? "btn-disabled opacity-40" : ""
            }`}
            title={t("gameManagement.sections.importTip", {
              defaultValue: "Replace game ordering per (category, geo) from a CSV file"
            })}
          >
            {importingCsv
              ? t("gameManagement.common.importing", { defaultValue: "Importing…" })
              : t("gameManagement.common.importCsv", { defaultValue: "Import CSV" })}
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleImportCsv}
              disabled={importingCsv}
            />
          </label>
        </div>
      </header>

      {loading && categories.length === 0 ? (
        <p className="py-8 text-center text-lg text-[rgba(6,7,21,1)] dark:text-white">
          {t("gameManagement.common.loading", { defaultValue: "Loading…" })}
        </p>
      ) : (
        <>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {orderable.map((c) => (
              <li key={c.key}>
                <button
                  type="button"
                  onClick={() => setEditingKey(c.key)}
                  className="w-full text-left rounded-lg border border-[rgba(132,_0,_168,_0.25)] dark:border-[rgba(255,255,255,0.1)] px-4 py-3 hover:bg-[rgba(132,_0,_168,_0.06)] dark:hover:bg-[rgba(132,_0,_168,_0.1)] text-[rgba(6,7,21,1)] dark:text-white transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium">{titleEnOf(c)}</div>
                    <span className="text-xs opacity-60">{c.source_type.toLowerCase()}</span>
                  </div>
                  <div className="text-xs opacity-60 font-mono mt-0.5">{c.key}</div>
                </button>
              </li>
            ))}
          </ul>

          {nonOrderable.length > 0 && (
            <>
              <div className="text-xs opacity-60 mt-2 text-[rgba(6,7,21,1)] dark:text-white">
                {t("gameManagement.sections.nonOrderableHeader", { defaultValue: "Non-orderable (game order is computed)" })}
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {nonOrderable.map((c) => (
                  <li key={c.key}>
                    <button
                      type="button"
                      disabled
                      className="w-full text-left rounded-lg border border-[rgba(132,_0,_168,_0.25)] dark:border-[rgba(255,255,255,0.1)] px-4 py-3 text-[rgba(6,7,21,1)] dark:text-white opacity-50 cursor-not-allowed"
                      title={t("gameManagement.sections.computedTip", {
                        defaultValue: "Game order is computed automatically for this section"
                      })}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium">{titleEnOf(c)}</div>
                        <span className="text-xs opacity-60">{t("gameManagement.sections.autoTag", { defaultValue: "auto" })}</span>
                      </div>
                      <div className="text-xs opacity-60 font-mono mt-0.5">{c.key}</div>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  )
}
